import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Form, Dropdown, ButtonGroup, Button, InputGroup } from "react-bootstrap";
import ToolbarButton from "../ToolbarButton";
import { useGridState, ALL_PAGE_SIZE } from "./useGridState";
import { aggregators, formatValue, formatTotal, getCellValue } from "./formatters";
import { exportToCsv } from "./exporters";
import FilterPopover from "./FilterPopover";

/* ============================================================================
   DataGrid — the single ERP grid. Selection, sorting, Excel-style FUNNEL
   filters (checkbox set + date operators, applied over the full dataset),
   global search, pagination (incl. "All"), column visibility, column REORDER
   (drag) + RESIZE (drag), density, pinned columns, totals footer (2-dp safe),
   and CSV export.

   Persistence (order / hidden / density / widths) is controlled-prop based —
   omit the props for session-only behaviour. Existing grids are unaffected:
   resize and the funnel are opt-in via `resizable` and `filter` on columns.
   ============================================================================ */

const filterKind = (col) => {
  if (!col.filter) return null;
  return col.filter === "date" ? "date" : "set";
};

const DataGrid = ({
  data = [],
  columns,
  getRowKey = (row, i) => row.id ?? i,
  loading = false,
  gridId,

  // selection
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isRowSelectable = () => true,

  // features
  search = true,
  pagination = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  initialSort = null,
  density = "compact",
  stickyHeader = true,
  maxHeight,
  footer = false,
  emptyMessage = "No records found",
  emptyIcon = "bi-inbox",
  reorderable = true,
  resizable = true,

  // controlled persistence
  columnOrder,
  onColumnOrderChange,
  hiddenColumns: controlledHidden,
  onHiddenChange,
  densityValue,
  onDensityChange,
  colWidths: controlledWidths,
  onColWidthsChange,

  // toolbar
  title,
  icon,
  toolbarLeft,
  toolbarRight,
  showColumnMenu = true,
  showDensityToggle = true,
  showFontToggle = true,
  fontScale,
  onFontScaleChange,
  exportable = false,

  // self-framing (replaces a SectionCard wrapper for grids)
  collapsible = false,
  collapsed,
  onCollapsedChange,
  defaultCollapsed = false,
  resizableHeight = false,
  bodyHeight,
  onBodyHeightChange,
  initialBodyHeight = 420,
  minBodyHeight = 160,
  maxBodyHeight = 1400,

  // server-side
  serverMode = false,
  totalRows: serverTotalRows,
  onQueryChange,

  // row interactions
  onRowClick,
  onRowDoubleClick,
  rowClassName,
  rowStyle,

  className = "",
  style = {},
}) => {
  const grid = useGridState({
    data,
    columns,
    initialPageSize: pageSize,
    initialSort,
    initialDensity: density,
    initialHidden: columns.filter((c) => c.hidden).map((c) => c.key),
    serverMode,
    totalRowsOverride: serverTotalRows,
    getRowKey,
    onQueryChange,
    controlledSelected: selectedKeys,
    onSelectionChange,
    controlledOrder: columnOrder,
    onColumnOrderChange,
    controlledHidden,
    onHiddenChange,
    controlledDensity: densityValue,
    onDensityChange,
    controlledWidths,
    onColWidthsChange,
  });

  const effWidth = useCallback(
    (col) => grid.colWidths[col.key] ?? (parseInt(col.width) || 140),
    [grid.colWidths]
  );

  // pinned-left sticky offsets
  const pinnedOffsets = useMemo(() => {
    const offsets = {};
    let acc = selectable ? 36 : 0;
    grid.visibleColumns.forEach((c) => {
      if (c.pinned === "left") { offsets[c.key] = acc; acc += effWidth(c); }
    });
    return { offsets };
  }, [grid.visibleColumns, selectable, effWidth]);

  // totals
  const totals = useMemo(() => {
    if (footer !== "totals") return null;
    const row = {};
    grid.visibleColumns.forEach((c) => {
      if (!c.total) return;
      const values = grid.filteredRows.map((r) => getCellValue(r, c));
      const agg = typeof c.total === "function" ? c.total : aggregators[c.total];
      row[c.key] = agg ? agg(values) : null;
    });
    return row;
  }, [grid.filteredRows, grid.visibleColumns, footer]);

  const densityClass = `ds-grid--${grid.density}`;

  // Grid text-size scale (controlled or internal). 1 = default.
  const [internalFont, setInternalFont] = useState(fontScale ?? 1);
  const fScale = fontScale ?? internalFont;
  const setFont = (v) => { if (onFontScaleChange) onFontScaleChange(v); if (fontScale == null) setInternalFont(v); };
  const FONT_STEPS = [["Small", 0.85], ["Medium", 1], ["Large", 1.18]];

  // collapse (controlled or internal)
  const [intCollapsed, setIntCollapsed] = useState(defaultCollapsed);
  const isCollapsed = collapsed ?? intCollapsed;
  const toggleCollapsed = () => {
    const v = !isCollapsed;
    if (onCollapsedChange) onCollapsedChange(v);
    if (collapsed == null) setIntCollapsed(v);
  };

  // body height (controlled or internal) — used when resizableHeight
  const [intBH, setIntBH] = useState(initialBodyHeight);
  const bh = bodyHeight ?? intBH;
  const setBH = (v) => {
    const nv = Math.min(maxBodyHeight, Math.max(minBodyHeight, v));
    if (onBodyHeightChange) onBodyHeightChange(nv);
    if (bodyHeight == null) setIntBH(nv);
  };
  const onBodyResizeDown = (e) => {
    e.preventDefault();
    const sy = e.clientY, sh = bh;
    document.body.classList.add("ds-row-resizing");
    const mv = (ev) => setBH(sh + (ev.clientY - sy));
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); document.body.classList.remove("ds-row-resizing"); };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };
  const handleExport = () => {
    const opts = typeof exportable === "object" ? exportable : {};
    exportToCsv({
      rows: grid.filteredRows,
      columns: grid.visibleColumns,
      filename: opts.filename || title?.toLowerCase().replace(/\s+/g, "_") || "export",
    });
  };
  const goTo = (p) => grid.setCurrentPage(Math.max(1, Math.min(p, grid.totalPages)));

  // ── Filter popover ──
  const [filterPop, setFilterPop] = useState(null); // { key, kind, top, left }
  const openFilter = (e, col) => {
    e.stopPropagation();
    if (filterPop?.key === col.key) { setFilterPop(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setFilterPop({ key: col.key, kind: filterKind(col), top: r.bottom + 2, left: r.left });
  };
  const popValues = useMemo(
    () => (filterPop && filterPop.kind === "set" ? grid.uniqueValues(filterPop.key) : []),
    [filterPop, grid]
  );

  // ── Column drag-reorder (pointer) ──
  const theadRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  dragRef.current = drag;
  const canReorder = (col) => reorderable && col.reorderable !== false && !!col.key;

  const onHeaderPointerDown = useCallback((e, col) => {
    if (!canReorder(col)) return;
    if (e.target.closest(".ds-grid-col-resize, .ds-grid-funnel, input, select, button, .sortable")) return;
    if (e.button != null && e.button !== 0) return;
    const startX = e.clientX;
    let started = false;
    const headerCells = () => Array.from(theadRef.current?.querySelectorAll("th[data-colkey]") || []);
    const computeOver = (clientX) => {
      let overKey = null, before = false;
      for (const th of headerCells()) {
        const k = th.getAttribute("data-colkey");
        if (k === col.key) continue;
        const r = th.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right) { overKey = k; before = clientX < r.left + r.width / 2; break; }
      }
      return { overKey, before };
    };
    const onMove = (ev) => {
      if (!started && Math.abs(ev.clientX - startX) < 4) return;
      if (!started) { started = true; document.body.classList.add("ds-grid-reordering"); }
      const { overKey, before } = computeOver(ev.clientX);
      setDrag({ key: col.key, label: col.label, x: ev.clientX, overKey, before });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.classList.remove("ds-grid-reordering");
      const d = dragRef.current;
      if (started && d && d.overKey) {
        let toKey = d.overKey;
        if (!d.before) {
          const keys = grid.orderedColumns.map((c) => c.key);
          const idx = keys.indexOf(d.overKey);
          toKey = keys[idx + 1] ?? null;
        }
        grid.moveColumn(d.key, toKey);
      }
      setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [grid, reorderable]);

  // ── Column resize (pointer) ──
  const onResizeDown = useCallback((e, col) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startW = effWidth(col);
    document.body.classList.add("ds-grid-resizing");
    const onMove = (ev) => grid.setColWidth(col.key, startW + (ev.clientX - startX));
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.classList.remove("ds-grid-resizing");
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [grid, effWidth]);

  useEffect(() => () => {
    document.body.classList.remove("ds-grid-reordering");
    document.body.classList.remove("ds-grid-resizing");
  }, []);

  // ── Pagination display ──
  const fromRow = grid.totalRows ? (grid.showingAll ? 1 : (grid.currentPage - 1) * grid.pageSize + 1) : 0;
  const toRow = grid.showingAll ? grid.totalRows : Math.min(grid.currentPage * grid.pageSize, grid.totalRows);

  const popColumn = filterPop ? grid.visibleColumns.find((c) => c.key === filterPop.key) : null;

  return (
    <div className={`ds-grid-wrapper ${className}`} style={style}>
      {/* Toolbar */}
      <div className="ds-grid-toolbar">
        {collapsible && (
          <button className="ds-grid-collapse" onClick={toggleCollapsed} title={isCollapsed ? "Expand" : "Collapse"}>
            <i className={`bi ${isCollapsed ? "bi-chevron-right" : "bi-chevron-down"}`} />
          </button>
        )}
        {title && (
          <div className="ds-grid-title">
            {icon && <i className={`bi ${icon} me-1`} />}{title}
            {isCollapsed && <span className="ds-grid-title__count">({grid.totalRows.toLocaleString("en-IN")})</span>}
          </div>
        )}
        {toolbarLeft}
        <div className="ds-grid-toolbar__spacer" />
        {search && (
          <InputGroup size="sm" className="ds-grid-search">
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control type="text" placeholder="Search..." value={grid.search}
              onChange={(e) => grid.setSearch(e.target.value)} />
            {grid.search && (
              <Button variant="outline-secondary" onClick={() => grid.setSearch("")}><i className="bi bi-x" /></Button>
            )}
          </InputGroup>
        )}
        {grid.activeFilterCount > 0 && (
          <ToolbarButton icon="bi-funnel-fill" tooltip="Clear all filters" variant="ghost"
            onClick={() => grid.clearFilters()} />
        )}
        {toolbarRight}
        {showDensityToggle && (
          <Dropdown align="end">
            <Dropdown.Toggle as={ToolbarButton} icon="bi-text-paragraph" tooltip="Density" variant="ghost" />
            <Dropdown.Menu>
              {["compact", "comfortable", "spacious"].map((d) => (
                <Dropdown.Item key={d} active={grid.density === d} onClick={() => grid.setDensity(d)}>
                  <i className={`bi ${grid.density === d ? "bi-check2" : "bi-blank"} me-2`} />
                  {d[0].toUpperCase() + d.slice(1)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {showFontToggle && (
          <Dropdown align="end">
            <Dropdown.Toggle as={ToolbarButton} icon="bi-fonts" tooltip="Text size" variant="ghost" />
            <Dropdown.Menu>
              {FONT_STEPS.map(([lbl, val]) => (
                <Dropdown.Item key={lbl} active={fScale === val} onClick={() => setFont(val)}>
                  <i className={`bi ${fScale === val ? "bi-check2" : "bi-blank"} me-2`} />{lbl}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {showColumnMenu && (
          <Dropdown align="end" autoClose="outside">
            <Dropdown.Toggle as={ToolbarButton} icon="bi-layout-three-columns" tooltip="Columns" variant="ghost" />
            <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto" }}>
              {grid.orderedColumns.filter((c) => c.hideable !== false && c.key).map((c) => (
                <Dropdown.Item key={c.key} as="button"
                  onClick={(e) => { e.preventDefault(); grid.toggleColumn(c.key); }}>
                  <i className={`bi ${grid.hiddenColumns.has(c.key) ? "bi-square" : "bi-check-square-fill text-primary"} me-2`} />
                  {c.label || c.key}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {exportable && (
          <ToolbarButton icon="bi-download" tooltip="Export CSV" variant="ghost" onClick={handleExport} />
        )}
      </div>

      {/* Scroll container */}
      {!isCollapsed && (
      <div className={`ds-grid-scroll ${densityClass}`} style={{ maxHeight: resizableHeight ? undefined : (maxHeight || "calc(100vh - 420px)"), height: resizableHeight ? bh : undefined, "--ds-grid-fs-scale": fScale }}>
        {loading && (
          <div className="ds-grid-loading"><div className="spinner-border text-primary spinner-border-sm" /></div>
        )}

        <table className={`ds-grid-table ${stickyHeader ? "ds-grid--sticky" : ""} ${resizable ? "ds-grid--fixed" : ""}`}>
          {resizable && (
            <colgroup>
              {selectable && <col style={{ width: 36 }} />}
              {grid.visibleColumns.map((col) => <col key={col.key} style={{ width: effWidth(col) }} />)}
            </colgroup>
          )}
          <thead ref={theadRef}>
            <tr>
              {selectable && (
                <th className="ds-grid-pinned" style={{ left: 0, width: 36 }}>
                  {selectable === "multi" && (
                    <Form.Check type="checkbox" checked={grid.allPageSelected}
                      onChange={() => grid.allPageSelected ? grid.deselectAllFiltered() : grid.selectAllFiltered(isRowSelectable)} />
                  )}
                </th>
              )}
              {grid.visibleColumns.map((col) => {
                const isSorted = grid.sort?.key === col.key;
                const pinLeft = col.pinned === "left" ? pinnedOffsets.offsets[col.key] : undefined;
                const draggable = canReorder(col);
                const isDragging = drag?.key === col.key;
                const isOver = drag?.overKey === col.key;
                const fk = filterKind(col);
                const fdesc = grid.columnFilter(col.key);
                const active = !!fdesc;
                const badge = !active ? null : (fdesc.type === "set" ? fdesc.set.size : "•");
                const canResize = resizable && col.resizable !== false;
                return (
                  <th key={col.key} data-colkey={col.key}
                    className={[
                      col.pinned === "left" ? "ds-grid-pinned" : "",
                      col.align ? `text-${col.align}` : "",
                      draggable ? "ds-grid-th--draggable" : "",
                      isDragging ? "ds-grid-th--dragging" : "",
                      isOver ? (drag.before ? "ds-grid-th--drop-before" : "ds-grid-th--drop-after") : "",
                      col.headerClass || "",
                    ].join(" ")}
                    style={{ width: resizable ? undefined : col.width, minWidth: col.minWidth, left: pinLeft, ...(col.headerStyle || {}) }}>
                    <div className="ds-grid-th"
                      onPointerDown={draggable ? (e) => onHeaderPointerDown(e, col) : undefined}>
                      {draggable && <i className="bi bi-grip-vertical ds-grid-th-grip" title="Drag to reorder" />}
                      <span className={col.sortable !== false ? "ds-grid-th-label sortable" : "ds-grid-th-label"}
                        onClick={() => col.sortable !== false && col.key && grid.toggleSort(col.key)}>
                        {col.label}
                        {col.sortable !== false && col.key && (
                          <i className={`bi ms-1 ${isSorted
                            ? grid.sort.direction === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill"
                            : "bi-arrow-down-up text-muted"}`} />
                        )}
                      </span>
                      {fk && (
                        <button type="button"
                          className={`ds-grid-funnel ${active ? "ds-grid-funnel--active" : ""}`}
                          title="Filter" onClick={(e) => openFilter(e, col)}>
                          <i className={`bi ${active ? "bi-funnel-fill" : "bi-funnel"}`} />
                          {badge != null && <span className="ds-grid-funnel__badge">{badge}</span>}
                        </button>
                      )}
                    </div>
                    {canResize && (
                      <div className="ds-grid-col-resize" title="Drag to resize"
                        onPointerDown={(e) => onResizeDown(e, col)} onClick={(e) => e.stopPropagation()} />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {grid.rows.length === 0 ? (
              <tr>
                <td colSpan={grid.visibleColumns.length + (selectable ? 1 : 0)} className="ds-grid-empty">
                  <i className={`bi ${emptyIcon} fs-2 d-block mb-2`} />
                  <div>{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              grid.rows.map((row, rowIndex) => {
                const key = getRowKey(row, rowIndex);
                const sel = grid.isSelected(row);
                const canSelect = isRowSelectable(row);
                const rc = typeof rowClassName === "function" ? rowClassName(row) : rowClassName;
                const rs = typeof rowStyle === "function" ? rowStyle(row) : rowStyle;
                return (
                  <tr key={key} className={`${sel ? "ds-grid-row--selected" : ""} ${rc || ""}`} style={rs}
                    onClick={(e) => onRowClick && onRowClick(row, e)}
                    onDoubleClick={(e) => onRowDoubleClick && onRowDoubleClick(row, e)}>
                    {selectable && (
                      <td className="ds-grid-pinned" style={{ left: 0, width: 36 }} onClick={(e) => e.stopPropagation()}>
                        <Form.Check type={selectable === "single" ? "radio" : "checkbox"} checked={sel} disabled={!canSelect}
                          onChange={() => {
                            if (selectable === "single") onSelectionChange && onSelectionChange(new Set([key]));
                            else grid.toggleRow(row);
                          }} />
                      </td>
                    )}
                    {grid.visibleColumns.map((col) => {
                      const rawVal = getCellValue(row, col);
                      const content = col.render ? col.render(rawVal, row, rowIndex) : formatValue(rawVal, col);
                      const pinLeft = col.pinned === "left" ? pinnedOffsets.offsets[col.key] : undefined;
                      const cc = typeof col.cellClass === "function" ? col.cellClass(rawVal, row) : col.cellClass;
                      const cs = typeof col.cellStyle === "function" ? col.cellStyle(rawVal, row) : col.cellStyle;
                      return (
                        <td key={col.key} data-colkey={col.key}
                          className={[
                            col.pinned === "left" ? "ds-grid-pinned" : "",
                            col.align ? `text-${col.align}` : "",
                            cc || "",
                          ].join(" ")}
                          style={{ left: pinLeft, ...(col.cellTruncate !== false ? { overflow: "hidden", textOverflow: "ellipsis" } : {}), ...(cs || {}) }}>
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>

          {footer === "totals" && totals && grid.rows.length > 0 && (
            <tfoot>
              <tr>
                {selectable && <td className="ds-grid-pinned" style={{ left: 0 }} />}
                {grid.visibleColumns.map((col, i) => {
                  const pinLeft = col.pinned === "left" ? pinnedOffsets.offsets[col.key] : undefined;
                  const val = totals[col.key];
                  let display = null;
                  if (val != null) display = formatTotal(val, col);
                  else if (i === 0) display = `Total: ${grid.totalRows.toLocaleString("en-IN")}`;
                  return (
                    <td key={col.key}
                      className={[col.pinned === "left" ? "ds-grid-pinned" : "", col.align ? `text-${col.align}` : ""].join(" ")}
                      style={{ left: pinLeft }}>
                      <strong>{display}</strong>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}

          {typeof footer === "function" && grid.rows.length > 0 && (
            <tfoot>{footer(grid.filteredRows, grid.visibleColumns)}</tfoot>
          )}
        </table>
      </div>
      )}

      {/* Body height resize handle */}
      {resizableHeight && !isCollapsed && (
        <div className="ds-grid-resize-y" onPointerDown={onBodyResizeDown} title="Drag to resize height">
          <i className="bi bi-grip-horizontal" />
        </div>
      )}

      {/* Drag ghost */}
      {drag && (
        <div className="ds-grid-drag-ghost" style={{ left: drag.x }}>
          <i className="bi bi-grip-vertical me-1" />{drag.label}
        </div>
      )}

      {/* Filter popover */}
      {filterPop && popColumn && (
        <FilterPopover
          column={popColumn}
          kind={filterPop.kind}
          valueType={popColumn.format === "number" || popColumn.format === "currency" ? "number" : "text"}
          anchor={{ top: filterPop.top, left: filterPop.left }}
          values={popValues}
          current={grid.columnFilter(filterPop.key)}
          onApply={(desc) => grid.setColumnFilter(filterPop.key, desc)}
          onClear={() => grid.clearColumnFilter(filterPop.key)}
          onSort={(dir) => grid.setSort({ key: filterPop.key, direction: dir })}
          onClose={() => setFilterPop(null)}
        />
      )}

      {/* Pagination */}
      {pagination && !isCollapsed && (
        <div className="ds-grid-pagination">
          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "var(--ds-fs-sm)" }}>
            {grid.selectedKeys.size > 0 && (
              <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
                {grid.selectedKeys.size} selected
              </span>
            )}
            <span>Showing {fromRow}–{toRow} of {grid.totalRows.toLocaleString("en-IN")}</span>
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" onClick={() => goTo(1)} disabled={grid.currentPage === 1}><i className="bi bi-chevron-double-left" /></Button>
              <Button variant="outline-secondary" onClick={() => goTo(grid.currentPage - 1)} disabled={grid.currentPage === 1}><i className="bi bi-chevron-left" /></Button>
              <Button variant="outline-secondary" disabled style={{ minWidth: 110 }}>Page {grid.currentPage} of {grid.totalPages}</Button>
              <Button variant="outline-secondary" onClick={() => goTo(grid.currentPage + 1)} disabled={grid.currentPage === grid.totalPages}><i className="bi bi-chevron-right" /></Button>
              <Button variant="outline-secondary" onClick={() => goTo(grid.totalPages)} disabled={grid.currentPage === grid.totalPages}><i className="bi bi-chevron-double-right" /></Button>
            </ButtonGroup>
            <Form.Select size="sm" value={grid.pageSize}
              onChange={(e) => { grid.setPageSize(parseInt(e.target.value)); goTo(1); }} style={{ width: 110 }}>
              {pageSizeOptions.map((s) => {
                const isAll = String(s).toLowerCase() === "all";
                return <option key={String(s)} value={isAll ? ALL_PAGE_SIZE : s}>{isAll ? "All" : `${s} / page`}</option>;
              })}
            </Form.Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataGrid;
